import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Button, Input, Select } from '@components';
import { useToast } from '../../../contexts/useToast';
import { DocumentsTable, EditDocumentModal, UploadDocumentModal } from '../components';
import { ConfirmDeleteModal } from '../components/ConfirmDeleteModal';
import { useKnowledgeBase } from '../hooks';
import { Document, KnowledgeBase, UploadDocumentData } from '../types';

const DOCUMENTS_POLL_INTERVAL_MS = 10000;

interface DocumentsTabProps {
  knowledgeBase: KnowledgeBase;
  store: ReturnType<typeof useKnowledgeBase>;
}

export const DocumentsTab: React.FC<DocumentsTabProps> = ({ knowledgeBase, store }) => {
  const { t } = useTranslation();
  const { showToast } = useToast();
  const {
    documents,
    filterLoading,
    filters,
    setFilters,
    uploadDocument,
    deleteDocument,
    updateDocument,
    fetchDocumentsWithFilters,
    fetchDocumentById,
  } = store;
  const [isUploadModalOpen, setIsUploadModalOpen] = useState(false);
  const [editingDocument, setEditingDocument] = useState<Document | null>(null);
  const [deletingDocument, setDeletingDocument] = useState<Document | null>(null);

  // Keeps the processing status of uploaded documents up to date
  useEffect(() => {
    const interval = setInterval(fetchDocumentsWithFilters, DOCUMENTS_POLL_INTERVAL_MS);
    return () => clearInterval(interval);
  }, [fetchDocumentsWithFilters]);

  const handleFilterChange = (newFilters: typeof filters) => {
    setFilters(newFilters);
    fetchDocumentsWithFilters();
  };

  const handleUploadDocument = async (data: UploadDocumentData) => {
    try {
      await uploadDocument(data);
      setIsUploadModalOpen(false);
      showToast({ type: 'success', message: t('knowledgeBase.documentMessages.uploaded') });
    } catch {
      showToast({ type: 'error', message: t('knowledgeBase.documentMessages.uploadFailed') });
    }
  };

  const handleEditDocumentSubmit = async (data: { title: string; tags: string }) => {
    if (!editingDocument) return;
    try {
      await updateDocument(editingDocument.id, data);
      showToast({ type: 'success', message: t('knowledgeBase.documentMessages.updated') });
      setEditingDocument(null);
    } catch {
      showToast({ type: 'error', message: t('knowledgeBase.documentMessages.updateFailed') });
    }
  };

  const confirmDeleteDocument = async () => {
    if (!deletingDocument) return;
    try {
      await deleteDocument(deletingDocument.id);
      showToast({ type: 'success', message: t('knowledgeBase.documentMessages.deleted') });
      setDeletingDocument(null);
    } catch {
      showToast({ type: 'error', message: t('knowledgeBase.documentMessages.deleteFailed') });
    }
  };

  const handleDownloadDocument = async (id: string) => {
    try {
      // Always fetch a fresh document to get a current signed URL (presigned URLs expire).
      const doc = await fetchDocumentById(id);
      if (doc && doc.file) {
        const link = document.createElement('a');
        link.href = doc.file;
        link.download = doc.title;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        showToast({
          type: 'success',
          message: t('knowledgeBase.documentMessages.downloadStarted'),
        });
      } else {
        showToast({
          type: 'error',
          message: t('knowledgeBase.documentMessages.downloadUnavailable'),
        });
      }
    } catch {
      showToast({ type: 'error', message: t('knowledgeBase.documentMessages.downloadFailed') });
    }
  };

  const findDocument = (id: string) => documents.find(doc => doc.id === id) ?? null;

  return (
    <div className="max-w-4xl space-y-6">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <h2 className="text-xl font-semibold text-gray-900 dark:text-white">
          {t('knowledgeBase.documentManagement')}
        </h2>
        <Button
          variant="gradient-multi"
          onClick={() => setIsUploadModalOpen(true)}
          className="flex items-center gap-2"
        >
          <span className="material-icons">upload_file</span>
          {t('knowledgeBase.uploadDocument')}
        </Button>
      </div>

      <div className="flex gap-4">
        <Input
          type="text"
          placeholder={t('knowledgeBase.searchDocuments')}
          value={filters.search}
          onChange={e => handleFilterChange({ ...filters, search: e.target.value })}
          className="w-80 h-10"
        />
        <Select
          value={filters.status}
          onChange={value =>
            handleFilterChange({ ...filters, status: Array.isArray(value) ? value[0] : value })
          }
          options={[
            { value: '', label: t('knowledgeBase.allStatus') },
            { value: 'uploaded', label: t('knowledgeBase.uploaded') },
            { value: 'processing', label: t('knowledgeBase.processing') },
            { value: 'processed', label: t('knowledgeBase.processed') },
            { value: 'failed', label: t('knowledgeBase.failed') },
          ]}
          className="w-40 h-10"
        />
      </div>

      <DocumentsTable
        documents={documents}
        onDownload={handleDownloadDocument}
        onEdit={id => setEditingDocument(findDocument(id))}
        onDelete={id => setDeletingDocument(findDocument(id))}
        onUploadDocument={() => setIsUploadModalOpen(true)}
        loading={filterLoading}
      />

      <UploadDocumentModal
        isOpen={isUploadModalOpen}
        onClose={() => setIsUploadModalOpen(false)}
        onSubmit={handleUploadDocument}
        knowledgeBaseId={knowledgeBase.id}
      />

      <EditDocumentModal
        isOpen={editingDocument !== null}
        onClose={() => setEditingDocument(null)}
        onSubmit={handleEditDocumentSubmit}
        document={editingDocument}
      />

      <ConfirmDeleteModal
        isOpen={deletingDocument !== null}
        onClose={() => setDeletingDocument(null)}
        onConfirm={confirmDeleteDocument}
        title={t('knowledgeBase.deleteDocument')}
        question={t('knowledgeBase.areYouSureDeleteDocument')}
        itemName={deletingDocument?.title ?? ''}
        warning={t('knowledgeBase.thisActionCannotBeUndoneDocument')}
        confirmLabel={t('knowledgeBase.deleteDocument')}
      />
    </div>
  );
};
